import { useCallback, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import Auth from '../services/auth';
import { loadUserFromStorage, storeUserWithTokenClaims } from '../utils/authStorage';
import {
  endSessionAndRedirect,
  getHttpStatus,
  getStoredAuthToken,
  isTransientServerError,
  LOGIN_REASONS,
  shouldEndSessionForAuthError,
} from '../utils/sessionAuth';

const REFRESH_INTERVAL_MS = 30 * 60 * 1000;
const REFRESH_THRESHOLD_MS = 60 * 60 * 1000;

interface UseSessionRefreshOptions {
  enabled?: boolean;
}

export function useSessionRefresh({ enabled = true }: UseSessionRefreshOptions = {}) {
  const refreshInFlight = useRef(false);

  const refreshIfNeeded = useCallback(async () => {
    if (!enabled || refreshInFlight.current) return;

    const token = getStoredAuthToken();
    if (!token) return;

    let timeUntilExpiration = 0;
    let permissionsMissingProductTier = false;
    try {
      const decoded = jwtDecode<{ exp?: number; permissions?: { productTier?: string } }>(token);
      if (!decoded?.exp) return;
      timeUntilExpiration = decoded.exp * 1000 - Date.now();
      permissionsMissingProductTier = !decoded.permissions?.productTier;
    } catch {
      return;
    }

    const nearingExpiry = timeUntilExpiration < REFRESH_THRESHOLD_MS;
    if (!nearingExpiry && !permissionsMissingProductTier) return;

    refreshInFlight.current = true;
    try {
      const result = await Auth.refreshSession();
      const stored = loadUserFromStorage();
      if (stored && result?.token) {
        const updated = storeUserWithTokenClaims({ ...stored, token: result.token });
        window.dispatchEvent(new CustomEvent('userStateChanged', { detail: updated }));
      }
    } catch (error: unknown) {
      const status = getHttpStatus(error);
      if (shouldEndSessionForAuthError(error, '/api/auth/refresh')) {
        endSessionAndRedirect(LOGIN_REASONS.session_expired);
      } else if (!isTransientServerError(status)) {
        console.warn('[auth] session refresh failed:', error);
      }
    } finally {
      refreshInFlight.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    void refreshIfNeeded();

    const intervalId = window.setInterval(() => {
      void refreshIfNeeded();
    }, REFRESH_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshIfNeeded();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, refreshIfNeeded]);
}
